/**
 * Composant Skeleton pour les états de chargement
 * Crée des placeholders animés qui imitent la structure du contenu
 */

interface SkeletonProps {
  className?: string
}

/**
 * Élément skeleton de base
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      aria-hidden="true"
    />
  )
}

/**
 * Skeleton pour le texte (ligne unique)
 */
export function SkeletonText({ width = 'w-full' }: { width?: string }) {
  return <Skeleton className={`h-4 ${width}`} />
}

/**
 * Skeleton pour un titre
 */
export function SkeletonTitle({ width = 'w-3/4' }: { width?: string }) {
  return <Skeleton className={`h-8 ${width}`} />
}

/**
 * Skeleton pour un badge
 */
export function SkeletonBadge() {
  return <Skeleton className="h-6 w-24 rounded-full" />
}

/**
 * Skeleton pour un pictogramme GHS
 */
export function SkeletonPictogram() {
  return <Skeleton className="w-14 h-14 rounded" />
}

/**
 * Skeleton pour le header de substance
 */
export function SubstanceHeaderSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <SkeletonBadge />
          </div>
          <SkeletonTitle width="w-2/3" />
          <Skeleton className="h-5 w-1/2 mt-2" />
        </div>
        <div className="flex flex-col items-end gap-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-3 w-16 mt-2" />
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100">
        <Skeleton className="h-3 w-20 mb-2" />
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-6 w-20 rounded" />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Skeleton pour la synthèse des risques
 */
export function RiskSummarySkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 border-l-4 border-l-gray-300">
      <Skeleton className="h-6 w-48 mb-6" />

      {/* Pictogrammes */}
      <div className="mb-6">
        <Skeleton className="h-4 w-40 mb-3" />
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <SkeletonPictogram key={i} />
          ))}
        </div>
      </div>

      {/* Codes H */}
      <div>
        <Skeleton className="h-4 w-48 mb-3" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Skeleton pour les onglets sources
 */
export function SourceTabsSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Onglets */}
      <div className="flex border-b border-gray-200">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex-1 min-w-[120px] px-4 py-3">
            <div className="flex items-center gap-2">
              <Skeleton className="w-2 h-2 rounded-full" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
        ))}
      </div>

      {/* Contenu */}
      <div className="p-6">
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100">
          <div>
            <Skeleton className="h-5 w-64 mb-2" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-10 w-36 rounded-lg" />
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <Skeleton className="h-5 w-48 mb-3" />
          <div className="space-y-2">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Skeleton pour le tableau EPI
 */
export function EpiTableSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 border-l-4 border-l-gray-300">
      <Skeleton className="h-6 w-64 mb-4" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
            <div className="flex-1">
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-20 mt-2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton pour la traçabilité
 */
export function TraceabilitySkeleton() {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Skeleton className="w-4 h-4 rounded" />
        <Skeleton className="h-4 w-40" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-2 bg-white rounded border border-gray-200">
            <div className="flex items-center gap-1.5">
              <Skeleton className="w-2 h-2 rounded-full" />
              <Skeleton className="h-4 w-12" />
            </div>
            <Skeleton className="h-3 w-20 mt-1" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton complet pour la page substance
 */
export function SubstancePageSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <SubstanceHeaderSkeleton />
      <RiskSummarySkeleton />
      <SourceTabsSkeleton />
      <EpiTableSkeleton />
      <TraceabilitySkeleton />
    </div>
  )
}

export default Skeleton
